import React from "react";
import { IoClose } from "react-icons/io5";
import { HiOutlineDownload } from "react-icons/hi";
import { FaFileLines } from "react-icons/fa6";
import { ImageUrl } from "../Common/API";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"];
const VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov", ".avi", ".mkv", ".m4v"];
const DOC_EXTENSIONS = [".pdf", ".txt", ".doc", ".docx", ".xls", ".xlsx", ".csv", ".ppt", ".pptx"];

export const resolveMediaUrl = (file) => {
  const path = file?.uploadPath || file?.filePath || file?.fileUrl || file?.url || "";
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = ImageUrl.endsWith("/") ? ImageUrl.slice(0, -1) : ImageUrl;
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
};

export const getFileExtension = (fileName = "") => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.substring(dotIndex).toLowerCase() : "";
};

export const getPreviewKind = (file) => {
  const type = (file?.fileType || "").toLowerCase();
  const ext = getFileExtension(file?.fileName);

  if (type === "folder") return "folder";
  if (type === "image" || type === "attachment" || IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (type === "video" || VIDEO_EXTENSIONS.includes(ext)) return "video";
  if (DOC_EXTENSIONS.includes(ext) || ["document", "pdf", "doc"].includes(type)) return "document";
  return "other";
};

export const getVideoMimeType = (fileName = "") => {
  const ext = getFileExtension(fileName);
  const map = {
    ".mp4": "video/mp4",
    ".webm": "video/webm",
    ".mov": "video/quicktime",
    ".avi": "video/x-msvideo",
    ".mkv": "video/x-matroska",
    ".m4v": "video/x-m4v",
  };
  return map[ext] || "video/mp4";
};

export const getDocViewerSrc = (fileExtention, assetFolderPath) => {
  if (!assetFolderPath) return "";
  if (fileExtention === ".pdf" || fileExtention === ".txt") {
    return assetFolderPath;
  }
  if (fileExtention === ".csv") {
    return `https://docs.google.com/gview?url=${encodeURIComponent(assetFolderPath)}&embedded=true`;
  }
  return `https://docs.google.com/viewer?url=${encodeURIComponent(assetFolderPath)}&embedded=true`;
};

const PreviewUploadModal = ({ previewFile, onClose }) => {
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!previewFile) return null;

  const mediaUrl = resolveMediaUrl(previewFile);
  const fileExtension = getFileExtension(previewFile.fileName);
  const previewKind = getPreviewKind(previewFile);
  const viewerSrc = previewKind === "document" ? getDocViewerSrc(fileExtension, mediaUrl) : "";

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      tabIndex="-1"
      aria-hidden="true"
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 sm:p-6 md:p-8"
    >
      <div className="relative w-[85vw] max-w-[1100px]" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="absolute -top-3 -right-3 z-20 w-9 h-9 rounded-full bg-white text-slate-700 hover:text-red-500 border border-slate-200 shadow-lg flex items-center justify-center transition-colors"
          onClick={onClose}
          title="Close Preview"
        >
          <IoClose size={22} />
        </button>

        <div className="w-full h-[70vh] max-h-[820px] bg-black rounded-2xl shadow-2xl overflow-hidden flex items-center justify-center">
          {!mediaUrl ? (
            <div className="flex flex-col items-center gap-3 text-center px-6">
              <FaFileLines size={36} className="text-slate-500" />
              <p className="text-sm font-semibold text-white">{previewFile.fileName}</p>
              <p className="text-xs text-slate-400">Preview URL is not available for this file.</p>
            </div>
          ) : previewKind === "image" ? (
            <img
              src={mediaUrl}
              alt={previewFile.fileName}
              className="max-w-full max-h-full object-contain"
            />
          ) : previewKind === "video" ? (
            <video
              key={mediaUrl}
              autoPlay
              controls
              playsInline
              className="max-w-full max-h-full w-full h-full object-contain"
              src={mediaUrl}
            >
              <source src={mediaUrl} type={getVideoMimeType(previewFile.fileName)} />
              Your browser does not support the video tag.
            </video>
          ) : previewKind === "document" ? (
            <iframe
              className="w-full h-full bg-white border-0"
              title="Document Viewer"
              src={viewerSrc}
            />
          ) : (
            <div className="flex flex-col items-center gap-4 text-center max-w-sm px-4">
              <div className="w-14 h-14 bg-slate-800 text-slate-400 rounded-2xl flex items-center justify-center shadow-inner">
                <FaFileLines size={28} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white truncate max-w-[300px]">{previewFile.fileName}</h3>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  Online preview is not available for this format.
                </p>
              </div>
              <a
                href={mediaUrl}
                download
                className="flex items-center gap-2 px-5 py-2.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors shadow-lg"
              >
                <HiOutlineDownload size={14} /> Download File
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewUploadModal;
